import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import api from "@/lib/api";
import Navbar from "@/components/Navbar";
import Footer from "@/components/Footer";
import { useAuth } from "@/context/AuthContext";
import { FavouriteBeats } from "@/components/FavouriteBeats";
import { CalendarDays, Clock, MapPin, Plus } from "lucide-react";

const statusStyle = {
  confirmed: "bg-[#D90429] text-white",
  pending: "bg-white/10 text-white/70 border border-white/20",
  rejected: "bg-transparent text-[#A0A0A0] border border-white/10 line-through",
};

export default function MyBookings() {
  const { user, login } = useAuth();
  const [bookings, setBookings] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user) { setLoading(false); return; }
    api.get("/bookings/me").then((r) => setBookings(r.data)).catch(() => {}).finally(() => setLoading(false));
  }, [user]);

  if (!user) {
    return (
      <div className="grain min-h-screen relative">
        <Navbar />
        <div className="px-6 py-32 text-center relative z-10">
          <h1 className="font-display text-3xl font-bold">Sign in to view your bookings</h1>
          <button onClick={login} data-testid="dashboard-login-button"
            className="mt-6 bg-[#D90429] hover:bg-[#ED2939] text-white px-8 py-4 font-bold uppercase tracking-wide">Sign In</button>
        </div>
        <Footer />
      </div>
    );
  }

  return (
    <div className="grain min-h-screen relative">
      <Navbar />
      <section className="px-6 md:px-12 lg:px-20 pt-16 pb-8 relative z-10 flex flex-wrap items-end justify-between gap-4">
        <div>
          <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D90429] mb-3">Control Room</p>
          <h1 className="font-display text-4xl md:text-5xl font-extrabold tracking-tighter">Hey, {user.name.split(" ")[0]}.</h1>
        </div>
        <Link to="/book" data-testid="new-booking-button"
          className="bg-[#D90429] hover:bg-[#ED2939] text-white px-7 py-3 font-bold uppercase tracking-wide text-sm inline-flex items-center gap-2 transition-all hover:-translate-y-1">
          <Plus className="h-4 w-4" /> New Booking
        </Link>
      </section>

      <section className="px-6 md:px-12 lg:px-20 py-8 relative z-10" data-testid="my-bookings-list">
        {loading ? (
          <p className="text-[#A0A0A0]">Loading...</p>
        ) : bookings.length === 0 ? (
          <div className="border border-white/10 bg-[#121212] p-16 text-center">
            <p className="text-[#A0A0A0]">No sessions yet. Time to make some noise.</p>
            <Link to="/book" className="mt-5 inline-block bg-[#D90429] text-white px-7 py-3 font-bold uppercase tracking-wide text-sm">Book your first session</Link>
          </div>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            {bookings.map((b, i) => (
              <motion.div key={b.booking_id} initial={{ opacity: 0, y: 20 }} animate={{ opacity: 1, y: 0 }} transition={{ delay: i * 0.05 }}
                className="border border-white/10 bg-[#121212] p-7" data-testid={`booking-card-${b.booking_id}`}>
                <div className="flex items-start justify-between">
                  <div>
                    <h3 className="font-display text-2xl font-bold">{b.room_name}</h3>
                    <p className="text-sm text-[#A0A0A0] mt-1">{b.package_name} · {b.hours}h</p>
                  </div>
                  <span className={`text-xs font-bold uppercase tracking-wider px-3 py-1 ${statusStyle[b.status]}`} data-testid={`booking-status-${b.booking_id}`}>{b.status}</span>
                </div>
                <div className="mt-5 space-y-2 text-sm text-white/80">
                  <p className="flex items-center gap-2"><CalendarDays className="h-4 w-4 text-[#D90429]" /> {b.date}</p>
                  <p className="flex items-center gap-2"><Clock className="h-4 w-4 text-[#D90429]" /> {b.start_time}</p>
                  {b.service_names.length > 0 && <p className="flex items-center gap-2"><MapPin className="h-4 w-4 text-[#D90429]" /> {b.service_names.join(", ")}</p>}
                </div>
                <div className="border-t border-white/10 mt-5 pt-4 flex items-center justify-between">
                  <span className="text-sm text-[#A0A0A0]">Total (pay at studio)</span>
                  <span className="font-display text-xl font-bold text-[#D90429]">RON {b.total_price}</span>
                </div>
              </motion.div>
            ))}
          </div>
        )}
      </section>
      <FavouriteBeats />
      <Footer />
    </div>
  );
}
